/**
 * Product drawer link-table sections (P15.3 / #416).
 *
 * Two LinkSection wirings:
 *   - ProductIBSLink     -> Product depends on IBS X (with optional role)
 *   - ProductCapabilityLink -> Product depends on Capability X
 *
 * Lives in its own file so the adapter doesn't carry inner
 * components (fast-refresh wants files that only export components
 * to be component-only).
 */
import { LinkSection } from "./LinkSection";

/**
 * P23.2 (#524): render `revenue_share` (Decimal as string from the
 * wire) as a percentage with 2dp; parse user input as either a
 * decimal (0.45) or a percentage suffix (45%) and convert back to
 * a JSON-serialisable string.
 */
export function formatRevenueShare(raw: unknown): string {
  if (raw === null || raw === undefined || raw === "") return "";
  const num = Number(raw);
  if (Number.isNaN(num)) return "";
  return `${(num * 100).toFixed(2)}%`;
}

export function parseRevenueShare(text: string): string | null | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const isPct = trimmed.endsWith("%");
  const numeric = Number(trimmed.replace(/%$/, ""));
  if (Number.isNaN(numeric)) return undefined;
  const share = isPct ? numeric / 100 : numeric;
  if (share < 0 || share > 1) return undefined;
  return share.toFixed(4);
}

export function ProductIBSLinks({ rowId }: { rowId: string }) {
  return (
    <LinkSection
      i18nPrefix="modules.foundation.admin.products.ibsLinks"
      listEndpoint={`/v1/admin/foundation/products/${rowId}/ibs-links`}
      deleteEndpoint={(targetId) =>
        `/v1/admin/foundation/products/${rowId}/ibs-links/${targetId}`
      }
      optionsEndpoint="/v1/admin/foundation/ibses?limit=500"
      targetFieldKey="ibs_service_id"
      displayFieldKey="role"
      queryKey={["foundation-admin", "products", rowId, "ibs-links"] as const}
      optionsQueryKey={["foundation-admin", "ibses", "parent-options"] as const}
      editableField={{
        key: "revenue_share",
        label: "Share",
        placeholder: "e.g. 45%",
        patchEndpoint: (targetId) =>
          `/v1/admin/foundation/products/${rowId}/ibs-links/${targetId}`,
        format: formatRevenueShare,
        parse: parseRevenueShare,
      }}
    />
  );
}

export function ProductCapabilityLinks({ rowId }: { rowId: string }) {
  return (
    <LinkSection
      i18nPrefix="modules.foundation.admin.products.capabilityLinks"
      listEndpoint={`/v1/admin/foundation/products/${rowId}/capability-links`}
      deleteEndpoint={(targetId) =>
        `/v1/admin/foundation/products/${rowId}/capability-links/${targetId}`
      }
      optionsEndpoint="/v1/admin/foundation/capabilities?limit=500"
      targetFieldKey="capability_id"
      queryKey={["foundation-admin", "products", rowId, "capability-links"] as const}
      optionsQueryKey={["foundation-admin", "capability", "parent-options"] as const}
    />
  );
}
